import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { useTheme } from "@mui/material";
import { Item } from "../../../../layouts/Theme";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import advancedFormatPlugin from 'dayjs/plugin/advancedFormat';
import weekOfYearPlugin from 'dayjs/plugin/weekOfYear';
import { useI18N } from '../../context/I18NContext';
import { useFlow } from '../../context/FlowContext';
import useWidgetStore from '../../store/useWidgetStore';
import useDataStore from '../../store/useDataStore';
const ANIMATION_SPEED = 250;
/**
 * How far ahead the picker looks, on first load only, for a week that has something to book.
 * Ten weeks is far enough to clear a holiday or a quiet stretch, and short enough that a widget
 * with no availability at all settles quickly instead of walking the calendar forever.
 */
const LOOK_AHEAD_WEEKS = 10;
const DAY_KEY = 'YYYY-MM-DD';
const MONTH_KEY = 'YYYYMM';
/**
 * The Monday of the week `date` falls in.
 *
 * Not `subtract(date.day() - 1)`: dayjs numbers Sunday 0, so that expression ADDS a day on a
 * Sunday and lands on the Monday of the week after — the one week you can least afford to get
 * wrong, since it is the week the widget opens on for anyone booking on a Sunday.
 */
const startOfWeek = (date) => date.subtract((date.day() + 6) % 7, 'day').startOf('day');
export default function WeekPicker(props) {
    const theme = useTheme();
    const flow = useFlow();
    const { t } = useI18N();
    const { language, extraDateMonth, selectedDate, setSelectedDate, selectedApptype, selectedResource, setSelectedResource, selectedCapacity, selectedCapacityGroups, setExtraDateMonth } = useWidgetStore();
    const { dates, loadedDateMonths } = useDataStore();
    const containerRef = useRef(null);
    // first-load look-ahead: how many weeks it has stepped, where it started, and whether it is
    // finished - which it also is the moment the visitor touches the picker themselves
    const lookAhead = useRef({ weeks: 0, done: false });
    const [currentDate, setCurrentDate] = useState();
    const [weeknumber, setWeeknumber] = useState();
    const [sliding, setSliding] = useState(false);
    const [direction, setDirection] = useState("next");
    const [enablePicker, setEnablePicker] = useState(true);
    const today = dayjs().startOf('day');
    // The week on screen, and the seven days in it.
    const weekStart = useMemo(() => currentDate ? startOfWeek(dayjs(currentDate)) : undefined, [currentDate]);
    const weekDays = useMemo(() => {
        if (!weekStart)
            return [];
        return Array.from({ length: 7 }, (unused, i) => weekStart.add(i, 'day'));
    }, [weekStart]);
    // Which days the backend actually offers, as YYYY-MM-DD. The date picker keys its
    // highlights on day-of-month because a month view cannot show two Septembers; a week view
    // crosses months and years, so it needs the full date.
    const bookableDays = useMemo(() => {
        const bookable = [];
        dates.forEach((date) => bookable.push(dayjs(date.time).format(DAY_KEY)));
        return bookable;
    }, [dates]);
    const isPast = (day) => day.isBefore(today, 'day');
    const isBookable = (day) => enablePicker && !isPast(day) && bookableDays.includes(day.format(DAY_KEY));
    const isInWeek = (day) => !!weekStart && !day.isBefore(weekStart, 'day') && day.isBefore(weekStart.add(7, 'day'), 'day');
    // A week that is entirely behind us has nothing to offer, so the same rule the date picker
    // applies to months (`disablePast`) stops the strip at the week we are in.
    const canGoBack = !!weekStart && weekStart.isAfter(startOfWeek(today), 'day');
    const slide = (delta, className) => {
        var _a;
        // first thing, and before any of the guards below can bail out: from the moment an arrow
        // is pressed the visitor is steering, and the look-ahead must not move the strip again
        lookAhead.current.done = true;
        if (!weekStart || sliding)
            return;
        setSliding(true);
        setDirection(delta === 1 ? "next" : "prev");
        (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add(className);
        setTimeout(() => {
            var _a;
            // off the week that is on screen when the animation ends, not off the one that was
            // there when it started - anything that moved the strip in between would otherwise
            // be undone, which reads as the picker jumping forward while you page back
            setCurrentDate((current) => (current
                ? startOfWeek(dayjs(current)).add(delta, 'week').toDate()
                : weekStart.add(delta, 'week').toDate()));
            (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove(className);
            setSliding(false);
        }, ANIMATION_SPEED);
    };
    const goBack = () => {
        if (canGoBack)
            slide(-1, 'slideLeft');
    };
    const goNext = () => slide(1, 'slideRight');
    useEffect(() => {
        if (!currentDate) {
            setCurrentDate(selectedDate && selectedDate[0] !== undefined
                ? new Date(selectedDate[0])
                : new Date());
        }
    }, []);
    useEffect(() => {
        if (currentDate) {
            dayjs.extend(advancedFormatPlugin);
            dayjs.extend(weekOfYearPlugin);
            let d = dayjs(currentDate).locale(language);
            setWeeknumber(parseInt(d.format('w')));
        }
    }, [currentDate]);
    // Same capacity gate as the date picker: until a valid number of people is chosen there is
    // nothing bookable to show, so every day stays greyed out rather than pretending otherwise.
    useEffect(() => {
        var _a, _b, _c, _d;
        setEnablePicker(selectedCapacity > 0
            && selectedCapacity >= ((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : 0)
            && selectedCapacity <= ((_d = (_c = props.config) === null || _c === void 0 ? void 0 : _c.max) !== null && _d !== void 0 ? _d : 100));
    }, [selectedCapacity, selectedApptype, selectedCapacityGroups]);
    useEffect(() => {
        if ((selectedResource === null || selectedResource === void 0 ? void 0 : selectedResource.length) === 0) {
            let hasResourceSelect = false;
            let selectViews = flow.views.filter((view) => [
                "select", "select1", "select2"
            ].includes(view.route.route));
            selectViews.forEach((view) => {
                var _a;
                let resourceSelect = (_a = view.components) === null || _a === void 0 ? void 0 : _a.filter((component) => {
                    return component.source === "resource";
                });
                if (resourceSelect && (resourceSelect === null || resourceSelect === void 0 ? void 0 : resourceSelect.length) > 0) {
                    hasResourceSelect = true;
                }
            });
            if (!hasResourceSelect) {
                // there is no resource select in this widget, so select -1 as a default
                setSelectedResource(-1);
            }
        }
    }, [selectedResource]);
    // getDates answers for one calendar month, the one selectedDate falls in. Keep that month on
    // the week in view — otherwise browsing forward keeps asking for the month you started in —
    // and have the second query cover the other month whenever the week spans two.
    useEffect(() => {
        var _a;
        if (!weekStart)
            return;
        const primaryMonth = (selectedDate && selectedDate[0] !== undefined
            ? dayjs(selectedDate[0])
            : dayjs()).format(MONTH_KEY);
        const weekMonths = [weekStart.format(MONTH_KEY), weekStart.add(6, 'day').format(MONTH_KEY)];
        if (!weekMonths.includes(primaryMonth)) {
            // Move the selection into the week on screen, which re-runs this effect, and the
            // branch below then picks up the second month. Not the Monday when the Monday has
            // been and gone: on the week we are in that would ask for times on a day that is
            // over, and "no times available on 24 August" is a confusing thing to read on the
            // 30th. The first day of the week that is still ahead is the sensible one.
            const firstAhead = (_a = weekDays.find((day) => !isPast(day))) !== null && _a !== void 0 ? _a : weekStart;
            setSelectedDate(firstAhead.toDate());
            return;
        }
        setExtraDateMonth(weekMonths.find((month) => month !== primaryMonth));
    }, [weekStart, selectedDate]);
    // The week-sized version of the date picker's updateBookableDates: the selection follows the
    // week on screen, so the times underneath always talk about the week you are looking at.
    //
    // The date picker leaves the selection alone when you page backwards, and a month view can
    // afford that - it shows the whole month, so the selected day is on screen either way. A week
    // strip cannot: paging back left the selection in the week you came from, and the times below
    // then named a day that was nowhere to be seen.
    useEffect(() => {
        var _a, _b;
        if (!weekStart || !enablePicker)
            return;
        const selected = selectedDate && selectedDate[0] !== undefined ? dayjs(selectedDate[0]) : undefined;
        if (selected && isInWeek(selected) && isBookable(selected))
            return;
        // the first day that can be booked; failing that the first one that has not been and
        // gone, so an empty week still says "no times on <a day of this week>" rather than
        // naming a day in the past
        const target = (_b = (_a = weekDays.find((day) => isBookable(day))) !== null && _a !== void 0 ? _a : weekDays.find((day) => !isPast(day))) !== null && _b !== void 0 ? _b : weekStart;
        // already there: setting it again would hand out a new array every render
        if (selected && selected.isSame(target, 'day'))
            return;
        setSelectedDate(target.toDate());
    }, [dates, weekStart, enablePicker, selectedDate]);
    // Whether what is on screen can be judged: getDates has answered for both months this week
    // touches. Until then an empty strip only means the answer has not arrived yet.
    const weekMonths = useMemo(() => weekStart ? [...new Set([weekStart.format(MONTH_KEY), weekStart.add(6, 'day').format(MONTH_KEY)])] : [], [weekStart]);
    const settled = useMemo(() => !!weekStart && weekMonths.every((month) => loadedDateMonths.includes(month)), [weekStart, weekMonths, loadedDateMonths]);
    // On first load the week the widget opens on is often empty - a quiet week, or simply the
    // weekend. Rather than showing a strip with nothing to click, step forward a week at a time
    // until one has availability. Every step asks the backend for the month it lands in, so this
    // waits for each answer before judging the next week, and gives up after LOOK_AHEAD_WEEKS,
    // returning to the week it started on.
    useEffect(() => {
        var _a;
        if (!weekStart || !enablePicker || lookAhead.current.done || !settled)
            return;
        if (weekDays.some((day) => isBookable(day))) {
            lookAhead.current.done = true;
            return;
        }
        if (lookAhead.current.weeks >= LOOK_AHEAD_WEEKS) {
            lookAhead.current.done = true;
            if (lookAhead.current.origin)
                setCurrentDate(lookAhead.current.origin.toDate());
            return;
        }
        lookAhead.current.origin = (_a = lookAhead.current.origin) !== null && _a !== void 0 ? _a : weekStart;
        lookAhead.current.weeks += 1;
        setCurrentDate(weekStart.add(1, 'week').toDate());
    }, [settled, dates, weekStart, enablePicker]);
    const days = useMemo(() => {
        if (!weekStart)
            return undefined;
        // three weeks wide: the strip is translated one week to the left, so the previous and
        // next week are already rendered either side of the visible one and the slide has
        // something to move to
        let start = weekStart.subtract(1, 'week');
        const days = [];
        for (let i = 0; i < 21; i++) {
            days.push(start.format(DAY_KEY));
            start = start.add(1, 'day');
        }
        return days;
    }, [weekStart]);
    const onClick = (event) => {
        lookAhead.current.done = true;
        setSelectedDate(dayjs(event.currentTarget.getAttribute('rel')).toDate());
    };
    return (_jsxs(_Fragment, { children: [days && (_jsxs(Stack, { direction: "row", spacing: 0, className: `weekpicker-${weeknumber}`, children: [_jsx(Item, { sx: {
                            textAlign: "center",
                            lineHeight: "64px",
                            width: '16px'
                        }, children: _jsx(IconButton, { onClick: goBack, disabled: !canGoBack || sliding, sx: {
                                padding: 0,
                                width: '24px',
                                "& svg": {
                                    marginLeft: '8px'
                                }
                            }, children: _jsx(ArrowBackIosIcon, {}) }) }), _jsx(Box, { sx: {
                            // background: '#c00',
                            overflow: 'hidden',
                            width: '434px',
                            "& .slideLeft": {
                                transform: "translate(0, 0) scale(1)",
                                transition: `all ${ANIMATION_SPEED}ms`
                            },
                            "& .slideRight": {
                                transform: "translate(-868px, 0) scale(1)",
                                transition: `all ${ANIMATION_SPEED}ms`
                            }
                        }, children: _jsx(Stack, { direction: "row", spacing: 0, sx: {
                                transform: "translate(-434px, 0) scale(1)",
                            }, ref: containerRef, children: days.map((day, index) => {
                                let d = dayjs(day).locale(language), bookable = isBookable(d), isToday = day === today.format(DAY_KEY), isSelected = selectedDate
                                    && selectedDate[0] !== undefined
                                    && dayjs(selectedDate[0]).format(DAY_KEY) === day;
                                return (_jsxs(Button, { rel: day, onClick: onClick, disabled: !bookable, sx: Object.assign(Object.assign(Object.assign({ textAlign: "center", background: '#fff', border: "#eee 1px solid", width: "62px", minWidth: "62px", height: '80px', display: 'inline-block', lineHeight: 1, padding: 0, fontSize: '1rem', borderRadius: 0 }, (!bookable && {
                                        "&.Mui-disabled": {
                                            backgroundColor: theme.palette.background.default,
                                            color: theme.palette.text.disabled,
                                            "& .MuiTypography-root": {
                                                color: theme.palette.text.disabled
                                            }
                                        }
                                    })), (isToday && {
                                        borderColor: theme.palette.text.primary
                                    })), (isSelected && {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                        "& .MuiTypography-root": {
                                            color: '#fff'
                                        },
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.main,
                                        }
                                    })), children: [d.format('dd'), _jsx(Typography, { variant: "h6", sx: {
                                                my: 0,
                                                fontWeight: 700,
                                                lineHeight: 1.4
                                            }, children: d.format('D') }), d.format('MMM')] }, index));
                            }) }) }), _jsx(Item, { sx: {
                            textAlign: "center",
                            lineHeight: "64px",
                            width: '16px'
                        }, children: _jsx(IconButton, { onClick: goNext, disabled: sliding, sx: {
                                padding: 0,
                                width: '24px'
                            }, children: _jsx(ArrowForwardIosIcon, {}) }) })] })), dates && dates.length === 0 && (_jsx(Typography, { variant: "subtitle1", sx: {
                    paddingX: 1,
                    marginY: 1,
                    fontSize: '1.25rem',
                    color: theme.palette.text.primary,
                    // same reason as the times heading: a drag off the arrows must not sweep it up
                    userSelect: 'none'
                }, children: t(selectedResource && (selectedResource === null || selectedResource === void 0 ? void 0 : selectedResource.length) > 0
                    ? selectedCapacity === 0
                        ? 'Kies eerst aantal personen'
                        : 'Er zijn geen dagen beschikbaar'
                    : 'Kies eerst een medewerker') }))] }));
}
